














import { Prop, Component, Vue } from 'vue-property-decorator'
import { trackFontVariantLoaded, isFontVariantLoadedTracked, isFontVariantLoaded } from 'bits/font_loading'

type NumberOfPaths = number
const COLORED_ICONS: Record<string, NumberOfPaths> = {
  'amazon-color': 2,
  'paypal-color': 3,
  'discover-color': 3,
  'jcb-color': 5,
  'mastercard-color': 3,
  'unionpay-color': 5,
  'amex-color': 2,
  'dinersclub-color': 3,
  'visa-color': 3,
  cupcake: 5,
  google: 4,
  windows: 4,
}
// @see https://material.io/design/usability/bidirectionality.html#mirroring-elements
const ORICONS_MIRRORING = [
  // Arrows
  'back',
  'next',
  'forward',
  'highlight_text',
  'left_arrow',
  'continue_large',
  // Text-related
  'subscript',
  'superscript',
  'bulleted_list',
  'numbered_list',
  'clipboard',
  'comment',
  'quote',
  'enlarge',
  'dock',
  'expand',
  'padlets',
  'send_back',
  'send_front',
  // Movement
  'transfer',
  'external_link',
  'external_link_outline',
  // Passage of time
  'undo',
  'redo',
]

/**
 * For list of icons:
 *
 *  - Go to https://icomoon.io/app/#/projects
 *  - Load appropriate project (*Immaterial* or *Oricons*)
 *  - Click *Generate Font* at the bottom
 *
 */
@Component({})
export default class OzIcon extends Vue {
  /**
   * If dir="rtl" is included, left-right arrows buttons are flipped
   * @default ltr
   */
  @Prop({ default: 'auto' }) dir!: 'ltr' | 'rtl' | 'auto'

  /**
   * Name of the icon to load
   */
  @Prop({
    type: String,
    required: true,
    validator: (name) => !!name,
  })
  name!: string

  /**
   * Determines the size of the icon
   */
  @Prop({
    type: [String, Number],
    required: false,
    default: 'normal',
    validator: (value) => typeof value == 'number' || ['small', 'normal', 'large'].includes(value),
  })
  size!: string

  /**
   * Determines if icon should be hidden from screen reader (i.e. only decorative). Default is true.
   */
  @Prop({
    type: String,
    required: false,
    default: 'true',
    validator: (value) => ['true', 'false'].includes(value),
  })
  ariaHidden!: boolean

  /**
   * Determines the icon font to load
   * @values immaterial, oricon
   */
  @Prop({
    type: String,
    required: false,
    default: 'oricon',
    validator: (value) => ['immaterial', 'oricon'].includes(value),
  })
  font!: string

  isFontLoaded = false

  get isColoredIcon(): boolean {
    return Object.keys(COLORED_ICONS).includes(this.name)
  }

  get isMirroringEnabled(): boolean {
    if (!this.name) return false
    if (this.font !== 'oricon') return false
    return ORICONS_MIRRORING.includes(this.name)
  }

  // @see https://material.io/design/usability/bidirectionality.html#mirroring-elements
  //
  // Use `inline-block` to force the element to be transformable:
  // https://stackoverflow.com/questions/14883250/css-transform-doesnt-work-on-inline-elements
  get rtlTransformClasses(): string {
    if (!this.isMirroringEnabled) return ''
    if (this.dir === 'rtl') return 'inline-block transform -scale-x-100'
    if (this.dir === 'auto') return 'inline-block rtl:transform rtl:-scale-x-100'
    return ''
  }

  get numOfPaths(): number {
    return COLORED_ICONS[this.name]
  }
  get iconClass(): string {
    if (this.font === 'immaterial') {
      return 'immaterial-icons'
    }
    return 'oricon'
  }

  get styles(): string {
    let sizeNumber = 24
    if (typeof this.size == 'number') {
      sizeNumber = this.size
    } else if (this.size === 'small') {
      sizeNumber = 18
    } else if (this.size === 'large') {
      sizeNumber = 32
    }
    if (!this.isFontLoaded) {
      return `visibility: hidden; font-size: ${sizeNumber}px; min-width:${sizeNumber}px; max-width:${sizeNumber}px; width: ${sizeNumber}px; height: ${sizeNumber}px;`
    }

    return `font-size: ${sizeNumber}px; max-width:${sizeNumber}px; width: ${sizeNumber}px; height: ${sizeNumber}px; overflow: visible;`
  }

  mounted(): void {
    const fontName = this.font == 'immaterial' ? 'immaterial' : 'oricons'
    const fontVariant = `24px ${fontName}`
    if (isFontVariantLoadedTracked(fontVariant)) {
      this.isFontLoaded = isFontVariantLoaded(fontVariant)
    } else {
      trackFontVariantLoaded(`24px ${fontName}`).then((isFontLoaded) => {
        this.isFontLoaded = isFontLoaded
      })
    }
  }
}
